import { useCallback, useMemo, useState } from "react"
import CodeMirror from "@uiw/react-codemirror"
import { html } from "@codemirror/lang-html"
import { oneDark } from "@codemirror/theme-one-dark"
import { Wand2 } from "lucide-react"
import { formatHtml } from "../utils/formatHtml"

type EditorHtmlSourceProps = {
  value: string
  height?: string | number
  onChange: (html: string) => void
}

const EditorHtmlSource = ({
  value,
  height = 400,
  onChange,
}: EditorHtmlSourceProps) => {
  const [isFormatting, setIsFormatting] = useState(false)

  const resolvedHeight =
    typeof height === "number" ? `${height}px` : height

  const editorHeight =
    typeof height === "number"
      ? `${Math.max(height - 40, 200)}px`
      : `calc(${height} - 40px)`

  const extensions = useMemo(() => [html()], [])

  const handleFormat = useCallback(async () => {
    setIsFormatting(true)
    try {
      const formatted = await formatHtml(value)
      onChange(formatted)
    } finally {
      setIsFormatting(false)
    }
  }, [onChange, value])

  return (
    <div
      className='tiptap-editor__html-source'
      style={{ minHeight: resolvedHeight }}
    >
      <div className='tiptap-editor__html-toolbar'>
        <button
          type='button'
          className='tiptap-editor__html-format-btn'
          onClick={handleFormat}
          disabled={isFormatting || !value.trim()}
        >
          <Wand2 size={14} />
          {isFormatting ? "Formatting…" : "Format HTML"}
        </button>
      </div>

      <div className='tiptap-editor__codemirror'>
        <CodeMirror
          value={value}
          height={editorHeight}
          theme={oneDark}
          extensions={extensions}
          onChange={onChange}
          basicSetup={{
            lineNumbers: true,
            foldGutter: true,
            highlightActiveLine: true,
            bracketMatching: true,
            autocompletion: false,
          }}
          placeholder='<p>Edit HTML here…</p>'
        />
      </div>
    </div>
  )
}

export default EditorHtmlSource
